import { PRESETS } from '@/volume/presets';
import { normalizedToHu } from '@/volume/tf';
import { TfEditor } from '@/volume/TfEditor';
import type { CleanRootStatus, CommissureId, LeafletStatus, TheaterSession, TheaterState } from './TheaterSession';

const LEAFLET_STATUS_TEXT: Record<LeafletStatus, string> = {
  off: '',
  loading: 'Loading the leaflet field…',
  ready: 'Leaflet surface from the run\u2019s cusp field (fill 1 = as measured, 2 = complete geometric leaflet).',
  missing: 'This frame has no leaflet field; the overlay is off until a frame with one is shown.',
  failed: 'The leaflet field could not be loaded.',
};

const CLEAN_ROOT_STATUS_TEXT: Record<CleanRootStatus, string> = {
  off: '',
  loading: 'Loading the clean volume…',
  ready: 'Clean Root: the Endocast look from outside; through the camera clip or the cutout the emptied root (the run’s clean volume and transfer function) with the leaflet surface, clipped 5 mm beyond the STJ and VBR rings.',
  missing: 'This frame has no clean volume; the tile renders the Endocast look until a frame with one is shown.',
  failed: 'The clean volume could not be loaded; the tile renders the Endocast look.',
};

const EMPTIED_STATUS_TEXT: Record<CleanRootStatus, string> = {
  off: '',
  loading: 'Loading the clean volume…',
  ready: 'The run’s clean render volume (blood pools emptied) under its clean transfer function.',
  missing: 'This frame has no clean volume; the raw CT is shown.',
  failed: 'The clean volume could not be loaded; the raw CT is shown.',
};

/** THEATER tab (MeasurementPanel.uxml theater-tab-content): presets, the transfer-function
 * editor, the shader section, commissural views and frame playback. */
export function TheaterPanel({ session, state }: { session: TheaterSession; state: TheaterState }) {
  const presets = PRESETS.filter((p) => state.availablePresets.includes(p.name));
  const hasCommissures = ['RN', 'RL', 'LN'].some((id) => session.prediction?.commissures[id as CommissureId]);
  const frameCount = session.frameCount;
  const [huMin, huMax] = session.windowHu();
  const [huLo, huHi] = session.huRange;
  const tfLocked = state.renderMode === 'mip';
  return (
    <div className="panel">
      <h3>Presets</h3>
      <div className="preset-tiles">
        {presets.map((p) => (
          <button key={p.name} className={`preset-tile ${state.presetName === p.name ? 'on' : ''}`} title={p.description} onClick={() => session.applyPreset(p.name)}>
            {p.name}
          </button>
        ))}
      </div>

      {state.cleanRootStatus !== 'off' && (
        <>
          <h3>{state.presetName === 'Emptied' ? 'Clean volume' : 'Clean root'}</h3>
          <div className="muted small">{(state.presetName === 'Emptied' ? EMPTIED_STATUS_TEXT : CLEAN_ROOT_STATUS_TEXT)[state.cleanRootStatus]}</div>
          {state.presetName !== 'Emptied' && (
            <>
              <Slider label={`Raw set opacity ${state.rawSetOpacity.toFixed(2)}`} value={state.rawSetOpacity} min={0} max={1} step={0.01} disabled={state.cleanRootStatus !== 'ready'} onChange={(v) => session.setRenderSetOpacity('raw', v)} />
              <Slider label={`Clean set opacity ${state.cleanSetOpacity.toFixed(2)}`} value={state.cleanSetOpacity} min={0} max={1} step={0.01} disabled={state.cleanRootStatus !== 'ready'} onChange={(v) => session.setRenderSetOpacity('clean', v)} />
            </>
          )}
        </>
      )}

      {state.leafletOverlay && (
        <>
          <h3>Leaflets</h3>
          <div className="muted small">{LEAFLET_STATUS_TEXT[state.leafletStatus]}</div>
          <Slider label={`Leaflet fill ${state.leafletFill.toFixed(2)}`} value={state.leafletFill} min={0} max={2} step={0.01} disabled={state.leafletStatus !== 'ready'} onChange={(v) => session.setLeafletFill(v)} />
          <Slider label={`Leaflet opacity ${state.leafletOpacity.toFixed(2)}`} value={state.leafletOpacity} min={0} max={1} step={0.01} disabled={state.leafletStatus !== 'ready'} onChange={(v) => session.setLeafletOpacity(v)} />
        </>
      )}

      <h3>Transfer function</h3>
      <TfEditor
        tf={state.tf}
        domain={state.tfDomain}
        window={state.window}
        histogram={state.histogram}
        huRange={session.huRange}
        renderMode={state.renderMode}
        samplingMultiplier={state.samplingMultiplier}
        boxOpacity={state.boxOpacity}
        caption={state.histogramCaption}
        onTfChange={(tf) => session.setTransferFunction(tf)}
        onWindowChange={(min, max) => session.setWindow(min, max)}
      />
      <label className="check">
        <input type="checkbox" checked={state.histogramRegionOnly} onChange={(e) => session.setHistogramRegionOnly(e.target.checked)} /> Histogram: rendered region only
      </label>

      <h3>Shader</h3>
      <label>
        Render mode
        <select value={state.renderMode} onChange={(e) => session.setRenderMode(e.target.value as 'dvr' | 'mip' | 'iso')}>
          <option value="dvr">Direct volume rendering</option>
          <option value="mip">Maximum intensity projection</option>
          <option value="iso">Isosurface</option>
        </select>
      </label>
      <div className="tf-row">
        <span className="label">Transfer function</span>
        {state.tfEdited && <span className="badge">edited</span>}
        <span className="spacer" />
        <button className="secondary small" disabled={tfLocked} title="Shift the transfer function down by 50 HU" onClick={() => session.nudgeTfShift(-50)}>
          −
        </button>
        <button className="secondary small" disabled={tfLocked} title="Shift the transfer function up by 50 HU" onClick={() => session.nudgeTfShift(50)}>
          +
        </button>
        <button className="secondary small" disabled={tfLocked} title="Reset the transfer function to the preset's curve (shift 0, stretch 1)" onClick={() => session.resetTransferFunction()}>
          Reset TF
        </button>
      </div>
      <Slider label={`Visibility min ${Math.round(normalizedToHu(state.window[0], huLo, huHi))} HU`} value={state.window[0]} min={0} max={1} step={0.001} onChange={(v) => session.setWindow(Math.min(v, state.window[1] - 0.001), state.window[1])} />
      <Slider label={`Visibility max ${Math.round(normalizedToHu(state.window[1], huLo, huHi))} HU`} value={state.window[1]} min={0} max={1} step={0.001} onChange={(v) => session.setWindow(state.window[0], Math.max(v, state.window[0] + 0.001))} />
      <div className="hu-fields">
        <label>
          Min HU
          <input type="number" value={huMin} min={huLo} max={huHi - 1} step={1} onChange={(e) => session.setWindowHu(Number(e.target.value), huMax)} />
        </label>
        <label>
          Max HU
          <input type="number" value={huMax} min={huLo + 1} max={huHi} step={1} onChange={(e) => session.setWindowHu(huMin, Number(e.target.value))} />
        </label>
      </div>
      <div className="readout">
        W {huMax - huMin} / L {Math.round((huMin + huMax) / 2)} HU
      </div>
      <Slider label={`TF shift ${state.tfShiftHu} HU`} value={state.tfShiftHu} min={-1024} max={1024} step={1} disabled={tfLocked} onChange={(v) => session.setTfShiftHu(v)} />
      <Slider label={`TF stretch ${state.tfStretch.toFixed(2)}`} value={state.tfStretch} min={0.5} max={2} step={0.01} disabled={tfLocked} onChange={(v) => session.setTfStretch(v)} />
      <Slider label={`Opacity ${state.boxOpacity.toFixed(2)}`} value={state.boxOpacity} min={0.05} max={1} step={0.01} onChange={(v) => session.setBoxOpacity(v)} />
      <Slider label={`Camera clip ${(state.cameraClip * 100).toFixed(0)} mm`} value={state.cameraClip} min={0} max={1} step={0.005} onChange={(v) => session.setCameraClip(v)} />
      <label className="check">
        <input type="checkbox" checked={state.lighting} onChange={(e) => session.setLighting(e.target.checked)} /> Lighting
      </label>
      <label>
        {state.reduceWhileMoving ? 'Quality at rest' : 'Quality'}
        <select
          value={`${state.samplingMultiplier}|${state.cubic ? 1 : 0}`}
          onChange={(e) => {
            const [m, c] = e.target.value.split('|');
            session.setQuality(Number(m), c === '1');
          }}
        >
          <option value="1|0">256 steps, trilinear</option>
          <option value="2|0">512 steps, trilinear</option>
          <option value="3|0">768 steps, trilinear</option>
          <option value="2|1">512 steps, tricubic</option>
          <option value="3|1">768 steps, tricubic (desktop)</option>
        </select>
      </label>
      <label className="check">
        <input type="checkbox" checked={state.reduceWhileMoving} onChange={(e) => session.setReduceWhileMoving(e.target.checked)} /> Reduce quality while moving the camera
      </label>
      <div className="muted small">Turn this on if rotating the camera feels laggy.</div>

      <h3>Misc</h3>
      <Slider label={`Gradient threshold (isosurface) ${state.gradientThreshold.toFixed(3)}`} value={state.gradientThreshold} min={0} max={0.2} step={0.001} disabled={state.renderMode !== 'iso'} onChange={(v) => session.setGradientThreshold(v)} />

      {hasCommissures && (
        <>
          <h3>Commissural views</h3>
          <div className="row wrap">
            {(
              [
                ['RN', 'Right-Non'],
                ['RL', 'Right-Left'],
                ['LN', 'Left-Non'],
              ] as [CommissureId, string][]
            ).map(([id, label]) => (
              <button
                key={id}
                className={`secondary ${state.commissureView === id ? 'on' : ''}`}
                disabled={!session.prediction?.commissures[id]}
                title={`Commissural view of ${label}, as in the report video (click again to exit)`}
                onClick={() => session.setCommissureView(id)}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      <h3>Frames</h3>
      <div className="row">
        <button className="secondary" disabled={frameCount < 2} title="Previous frame" onClick={() => session.prevFrame()}>
          Prev
        </button>
        <button className="secondary" disabled={frameCount < 2} title={state.playing ? 'Pause playback' : 'Play frames'} onClick={() => (state.playing ? session.pause() : session.play())}>
          {state.playing ? 'Pause' : 'Play'}
        </button>
        <button className="secondary" disabled={frameCount < 2} title="Next frame" onClick={() => session.nextFrame()}>
          Next
        </button>
        <span className="muted small">
          Frame {state.frame + 1} / {frameCount}
        </span>
      </div>
      <Slider label={`Speed (fps) ${state.fps.toFixed(1)}`} value={state.fps} min={0.5} max={15} step={0.5} onChange={(v) => session.setFps(v)} />
    </div>
  );
}

export function Slider({ label, value, min, max, step, disabled, onChange }: { label: string; value: number; min: number; max: number; step: number; disabled?: boolean; onChange: (v: number) => void }) {
  return (
    <label className="slider">
      <span>{label}</span>
      <input type="range" min={min} max={max} step={step} value={value} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))} />
    </label>
  );
}
